import { createApiClient } from "@kopi/api-client";
import type { ApiError } from "@kopi/contracts";

const API_BASE_URL = import.meta.env.VITE_API_URL ?? "http://localhost:3000";

export const api = createApiClient(API_BASE_URL);

/** Absolute URL of a relative API path the API hands out (e.g. a product photo). */
export function apiUrl(path: string): string {
  return new URL(path, API_BASE_URL).toString();
}

/** Thrown for any non-2xx API response; carries the PRD §20 error envelope when present. */
export class ApiRequestError extends Error {
  constructor(
    readonly status: number,
    readonly envelope: Partial<ApiError>,
  ) {
    super(envelope.message ?? `Request failed with status ${status}`);
  }

  get code(): string | undefined {
    return this.envelope.code;
  }
}

/**
 * Unwraps an Eden response or throws ApiRequestError. Eden folds the auth macro's 401 envelope into
 * the success type; at runtime non-2xx responses always arrive as `error`, so it's excluded here.
 */
export function unwrap<T>(
  response: { data: T; error: null } | { data: null; error: { status: unknown; value: unknown } },
): Exclude<T, ApiError> {
  if (response.error) {
    const value = response.error.value;
    throw new ApiRequestError(
      Number(response.error.status),
      typeof value === "object" && value !== null ? (value as Partial<ApiError>) : {},
    );
  }
  return response.data as Exclude<T, ApiError>;
}

export function isUnauthenticated(error: unknown): boolean {
  return error instanceof ApiRequestError && error.status === 401;
}
